<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { computed, ref } from 'vue';
import SupportFormModal from '@/Components/SupportFormModal.vue';
import ThemeSwitcher from '@/Components/ThemeSwitcher.vue';
import DemoBanner from '@/Components/DemoBanner.vue';
import SocialProof from '@/Components/HealthReport/SocialProof.vue';
import FeatureGrid from '@/Components/HealthReport/FeatureGrid.vue';
import FAQSection from '@/Components/HealthReport/FAQSection.vue';
import TrustBadges from '@/Components/HealthReport/TrustBadges.vue';
import IndustryGrid from '@/Components/Sales/IndustryGrid.vue';
import StepFlow from '@/Components/Sales/StepFlow.vue';
import TestimonialGrid from '@/Components/Sales/TestimonialGrid.vue';

const props = defineProps({
    canLogin: {
        type: Boolean,
    },
    canRegister: {
        type: Boolean,
    },
    laravelVersion: {
        type: String,
        required: true,
    },
    phpVersion: {
        type: String,
        required: true,
    },
    isDemo: {
        type: Boolean,
        default: false,
    },
    calendlyUrl: {
        type: String,
        default: null,
    },
});

const showSupportModal = ref(false);
const showThankYouMessage = ref(false);

const openSupportModal = () => {
    showSupportModal.value = true;
    showThankYouMessage.value = false;
};

const closeSupportModal = () => {
    showSupportModal.value = false;
};

const handleSupportSubmitted = () => {
    closeSupportModal();
    showThankYouMessage.value = true;
    setTimeout(() => {
        showThankYouMessage.value = false;
    }, 5000);
};

const bookDemoUrl = computed(
    () => props.calendlyUrl || 'mailto:sales@repjump.com?subject=15-min%20Demo%20Request',
);

const socialProofStats = [
    { id: 1, type: 'stats', value: 5000, label: 'Reviews Generated' },
    { id: 2, type: 'stats', value: 4.9, label: 'Client Satisfaction Rating' },
    { id: 3, type: 'stats', value: 500, label: 'Active Business Clients' },
];

const problemFeatures = [
    {
        icon: 'trending',
        title: 'Not Asking Consistently',
        description: 'You ask some customers for reviews but forget others. No systematic approach means missed opportunities every day.',
        stat: '70% customers never asked',
    },
    {
        icon: 'star',
        title: 'Manual Process is Slow',
        description: 'Asking customers in person is awkward and time-consuming. Staff forget or feel uncomfortable requesting reviews.',
        stat: '2-3 reviews/month',
    },
    {
        icon: 'chart',
        title: 'Missing the Window',
        description: 'By the time you ask (if you remember), customers have moved on and the moment of satisfaction has passed.',
        stat: '80% response drop-off',
    },
];

const howItWorksSteps = [
    {
        icon: 'qrcode',
        title: 'Customer Visits Your Business',
        description: 'After service, customers scan your QR code or enter info via your custom link.',
        note: 'Takes customers 10 seconds',
    },
    {
        icon: 'chat',
        title: 'Automated SMS Sent',
        description: 'Rep Jump automatically sends a personalized SMS at the perfect time asking for feedback.',
        note: 'Sent at optimal timing for max response',
    },
    {
        icon: 'route',
        title: 'Smart Rating Routing',
        description: 'Happy customers (5★) get directed to leave Google reviews. Others provide private feedback.',
        note: 'Protects your public reputation',
    },
    {
        icon: 'trending',
        title: 'Watch Your Reviews Grow',
        description: 'Track everything in your dashboard. See reviews, response rates, and feedback in real-time.',
        note: 'Average 10-20+ new reviews per month',
    },
];

const keyFeatures = [
    {
        icon: 'chat',
        title: 'Automated SMS Requests',
        description: 'Every customer gets a review request via text message. No manual work required.',
    },
    {
        icon: 'trending',
        title: 'Smart Rating Routing',
        description: '5 star ratings go to Google. 1-4 star ratings become private feedback you can address.',
    },
    {
        icon: 'lightning',
        title: 'QR Codes & Short Links',
        description: 'Make it easy for customers to enter their info with QR codes, short URLs, and custom forms.',
    },
    {
        icon: 'chart',
        title: 'Real-Time Dashboard',
        description: 'See exactly what\'s working with detailed analytics on review requests and conversion rates.',
    },
    {
        icon: 'cog',
        title: 'AI Review Responses',
        description: 'Generate professional, search engine friendly responses to reviews instantly with AI-powered suggestions.',
    },
    {
        icon: 'users',
        title: 'Automated Follow-Ups',
        description: 'Customers who don\'t respond get a friendly follow-up 24 hours later to boost conversion.',
    },
];

const withoutItems = [
    'Manually asking customers (awkward & time-consuming)',
    'Inconsistent — only a fraction of customers ever get asked',
    'Missing the window when customers are happiest',
    'Negative reviews land publicly on Google',
    'Lower Google Maps ranking, fewer new customer leads',
];

const withItems = [
    'Automated SMS sent to every customer (100% consistency)',
    'Perfect timing captures customers at peak satisfaction',
    'Smart routing sends 5★ to Google, others to private feedback',
    'Public reputation protected from one-off bad days',
    'Climb the local Maps ranking and get found by more searchers',
];

const industries = [
    {
        name: 'Home Services',
        icon: 'home',
        description: 'HVAC, plumbing, electrical, landscaping — capture every job with SMS-first review requests.',
    },
    {
        name: 'Healthcare',
        icon: 'health',
        description: 'Dental, chiropractic, medical clinics — collect reviews while staying patient-friendly.',
    },
    {
        name: 'Fitness',
        icon: 'fitness',
        description: 'Gyms, personal trainers, yoga studios — turn member milestones into 5-star reviews.',
    },
    {
        name: 'Restaurants',
        icon: 'restaurant',
        description: 'Cafés, full-service, takeout — QR codes on receipts trigger fresh reviews instantly.',
    },
    {
        name: 'Auto',
        icon: 'car',
        description: 'Repair shops, detailing, dealerships — every service ticket becomes a review opportunity.',
    },
];

const testimonials = [
    {
        id: 1,
        type: 'testimonials',
        value: 'We went from 1 review per month (if we were lucky) to 15-20 consistently. Rep Jump completely changed our online reputation. The automated system just works.',
        author: 'Mike Thompson',
        role: 'Owner',
        company: 'Thompson HVAC Services',
    },
    {
        id: 2,
        type: 'testimonials',
        value: 'The smart routing is brilliant. We catch unhappy customers before they post negative reviews publicly. That alone is worth the investment.',
        author: 'Sarah Chen',
        role: 'Marketing Director',
        company: 'Elite Dental Care',
    },
    {
        id: 3,
        type: 'testimonials',
        value: 'Setup took 10 minutes and we got our first review the same day. Now it runs on autopilot and we consistently get 5-star reviews.',
        author: 'David Kumar',
        role: 'Franchise Owner',
        company: 'ProFit Gym',
    },
];

const faqs = [
    {
        question: 'How do I get more Google reviews for my business?',
        answer: 'The most effective way to get more Google reviews is to automate the review request process. Rep Jump sends automated SMS messages to your customers after their visit, making it easy for happy customers to leave 5-star reviews with just a few taps. Our system handles timing, follow-ups, and routing automatically so you get consistent results.',
    },
    {
        question: 'Is automated review collection against Google\'s policy?',
        answer: 'No, as long as you\'re not incentivizing reviews or being deceptive. Rep Jump follows all Google guidelines by simply asking customers to share their honest experience. We never offer incentives or manipulate the process. We just make it convenient for customers who want to leave reviews.',
    },
    {
        question: 'How quickly will I see more reviews?',
        answer: 'Most businesses start seeing new reviews within the first week. On average, our clients receive 10-20 new reviews per month, with some high-traffic businesses receiving 50+ reviews monthly. Results depend on your customer volume and how consistently you collect customer information.',
    },
    {
        question: 'What happens to negative feedback?',
        answer: 'This is one of Rep Jump\'s most powerful features. When a customer rates you 1-4 stars, they\'re asked to provide private feedback instead of being sent to Google. This gives you a chance to address their concerns directly and potentially turn them into happy customers, while protecting your public reputation.',
    },
    {
        question: 'How much does Rep Jump cost?',
        answer: 'We offer flexible pricing based on your business size and customer volume. Plans start at an affordable monthly rate with no long-term contracts. Schedule a free demo to discuss pricing options that fit your business. The ROI from increased reviews typically pays for itself many times over.',
    },
    {
        question: 'Do I need any special equipment?',
        answer: 'No special equipment needed! You just need a way for customers to enter their information (QR code, tablet, or verbal). We provide printable QR codes and custom links. Everything else happens automatically through our system via SMS.',
    },
    {
        question: 'Can I customize the messages sent to customers?',
        answer: 'Yes! You can fully customize all SMS messages including review requests, follow-ups, and thank you messages. Add your business name, personalize the tone, and adjust timing to match your brand and customer experience.',
    },
    {
        question: 'How is this different from just asking customers myself?',
        answer: 'Manual asking is inconsistent, awkward, and time-consuming. You forget to ask some customers, staff may not do it consistently, timing is often wrong, and customers simply forget once they\'re home. Rep Jump ensures EVERY customer gets asked at the PERFECT time, automatically. It\'s like having a dedicated review manager working 24/7.',
    },
];

const trustBadges = [
    { id: 'secure', icon: 'shield', label: '100% Secure & Private' },
    { id: 'setup', icon: 'lightning', label: 'Setup in 5 Minutes' },
    { id: 'support', icon: 'users', label: 'Dedicated Support Team' },
];
</script>

<template>
    <Head title="RepJump — Turn Happy Customers into 5-Star Google Reviews" />

    <DemoBanner :isDemo="$page.props.isDemo" :currentEnvironment="$page.props.currentEnvironment" />

    <div v-if="$page.props.flash?.error" class="bg-red-500 text-white text-center py-3 px-4 text-sm font-medium">
        {{ $page.props.flash.error }}
    </div>

    <div class="min-h-screen bg-gray-900">
        <!-- Header + Hero (shared gradient/grid background) -->
        <div class="relative isolate overflow-hidden">
            <svg
                class="absolute inset-0 -z-10 size-full stroke-white/10 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
                aria-hidden="true"
            >
                <defs>
                    <pattern id="welcome-hero-pattern" width="200" height="200" x="50%" y="-1" patternUnits="userSpaceOnUse">
                        <path d="M.5 200V.5H200" fill="none" />
                    </pattern>
                </defs>
                <svg x="50%" y="-1" class="overflow-visible fill-gray-800/20">
                    <path
                        d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                        stroke-width="0"
                    />
                </svg>
                <rect width="100%" height="100%" stroke-width="0" fill="url(#welcome-hero-pattern)" />
            </svg>
            <div
                class="absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:left-48 lg:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]"
                aria-hidden="true"
            >
                <div
                    class="aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-brand-accent to-brand-secondary opacity-20"
                    style="clip-path: polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)"
                ></div>
            </div>

            <header class="relative z-20">
                <div class="mx-auto max-w-7xl px-6 lg:px-8">
                    <div class="grid grid-cols-2 items-center gap-2 py-6 lg:py-10">
                        <div class="flex">
                            <Link :href="route('welcome')">
                                <img src="/images/logo/rep-jump-logo2.png" alt="Rep Jump Logo" class="w-32 sm:w-40 md:w-48 lg:max-w-sm" />
                            </Link>
                        </div>
                        <nav v-if="canLogin" class="-mx-3 flex flex-1 justify-end items-center space-x-4">
                            <ThemeSwitcher />

                            <Link
                                v-if="$page.props.auth?.user"
                                :href="route('dashboard')"
                                class="rounded-md px-3 py-2 text-white ring-1 ring-transparent transition hover:text-white/80 focus:outline-none focus-visible:ring-white"
                            >
                                Dashboard
                            </Link>

                            <template v-else>
                                <Link
                                    :href="route('login')"
                                    class="rounded-md px-3 py-2 text-white ring-1 ring-transparent transition hover:text-white/80 focus:outline-none focus-visible:ring-white"
                                >
                                    Log in
                                </Link>

                                <Link
                                    v-if="canRegister"
                                    :href="route('register')"
                                    class="rounded-md px-3 py-2 text-white ring-1 ring-transparent transition hover:text-white/80 focus:outline-none focus-visible:ring-white"
                                >
                                    Register
                                </Link>
                            </template>
                        </nav>
                    </div>
                </div>
            </header>

            <main class="relative">
                <div class="mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-32 lg:flex lg:items-center lg:px-8 lg:py-16">
                    <div class="mx-auto max-w-2xl shrink-0 lg:mx-0 lg:max-w-xl lg:pt-8">
                        <div class="mb-5">
                            <span class="inline-flex items-center rounded-full bg-brand-primary/10 px-3 py-1 text-sm font-medium text-brand-accent ring-1 ring-inset ring-brand-primary/20">
                                🚀 Trusted by 500+ Businesses
                            </span>
                        </div>
                        <h1 class="text-pretty text-4xl font-semibold tracking-tight text-white sm:text-6xl lg:text-7xl">
                            Turn Happy Customers into 5-Star ⭐ Google Reviews
                        </h1>
                        <p class="mt-6 text-pretty text-lg font-medium text-gray-300 sm:text-xl/8">
                            Automate review collection over SMS. Watch your 5-star Google reviews grow on autopilot.
                        </p>
                        <div class="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                            <Link
                                v-if="canRegister"
                                :href="route('register')"
                                class="rounded-md bg-brand-primary px-6 py-3 text-center text-base font-semibold text-white shadow-sm hover:bg-brand-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary transition-colors"
                            >
                                Get Started
                            </Link>
                            <a
                                :href="bookDemoUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="rounded-md border border-white/30 px-6 py-3 text-center text-base font-semibold text-white hover:bg-white/10 transition-colors"
                            >
                                Book a Free 15-Min Demo <span aria-hidden="true">→</span>
                            </a>
                        </div>
                    </div>
                    <div class="mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
                        <div class="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
                            <img
                                src="/images/landing/dark-app-screenshot.png"
                                alt="Rep Jump dashboard"
                                width="2432"
                                height="1442"
                                class="w-[57rem] rounded-md bg-white/5 shadow-2xl ring-1 ring-white/10"
                            />
                        </div>
                    </div>
                </div>
            </main>
        </div>

        <!-- Social proof stats -->
        <section class="py-16 bg-gray-800 bg-opacity-50 border-y border-gray-700">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SocialProof :items="socialProofStats" variant="stats" />
            </div>
        </section>

        <!-- Problem -->
        <section class="py-20 bg-gray-900">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-16">
                    <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">
                        Why Your Competitors Have More Google Reviews Than You
                    </h2>
                    <p class="text-xl text-gray-400 max-w-3xl mx-auto">
                        It's not because they're better. They just have a system.
                    </p>
                </div>
                <FeatureGrid :features="problemFeatures" :columns="3" />
            </div>
        </section>

        <!-- How It Works -->
        <section class="py-20 bg-gray-800 bg-opacity-30">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-14">
                    <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">How Rep Jump Works</h2>
                    <p class="text-xl text-gray-400 max-w-2xl mx-auto">
                        Four simple steps to transform happy customers into 5-star Google reviews
                    </p>
                </div>
                <StepFlow :steps="howItWorksSteps" />
            </div>
        </section>

        <!-- Local SEO Flywheel -->
        <section class="relative overflow-hidden py-20 bg-gray-900">
            <div class="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-primary/10 via-gray-900 to-gray-900"></div>

            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-14">
                    <span class="inline-flex items-center rounded-full bg-brand-primary/10 px-3 py-1 text-sm font-medium text-brand-accent ring-1 ring-inset ring-brand-primary/20 mb-4">
                        📍 Local SEO Flywheel
                    </span>
                    <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">
                        More Reviews = More New Customers Found You
                    </h2>
                    <p class="text-xl text-gray-400 max-w-3xl mx-auto">
                        Google Maps prioritizes businesses with more recent, high-rated reviews.
                        Every review you collect lifts your local ranking — putting your business
                        in front of customers who would have never found you otherwise.
                    </p>
                </div>

                <div class="grid md:grid-cols-3 gap-6 lg:gap-4 max-w-6xl mx-auto">
                    <!-- Step 1 -->
                    <div class="relative">
                        <div class="flex h-full flex-col rounded-2xl border border-gray-700 bg-gray-800/60 p-6 transition-all hover:border-brand-primary hover:-translate-y-1">
                            <div class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-primary/10 ring-1 ring-brand-primary/20">
                                <svg class="h-6 w-6 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                            </div>
                            <h3 class="text-lg font-semibold text-white mb-2">More Reviews Collected</h3>
                            <p class="text-sm text-gray-400 mb-4 flex-1">
                                Rep Jump's automated SMS captures reviews from every customer — not just the loud ones.
                            </p>
                            <p class="text-sm font-semibold text-brand-accent">10–20+ new reviews / month</p>
                        </div>
                        <div class="hidden md:flex absolute top-1/2 -right-3 lg:-right-2 -translate-y-1/2 z-10 h-8 w-8 items-center justify-center rounded-full bg-gray-900 ring-2 ring-brand-primary/40">
                            <svg class="h-4 w-4 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>

                    <!-- Step 2 -->
                    <div class="relative">
                        <div class="flex h-full flex-col rounded-2xl border border-gray-700 bg-gray-800/60 p-6 transition-all hover:border-brand-primary hover:-translate-y-1">
                            <div class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-primary/10 ring-1 ring-brand-primary/20">
                                <svg class="h-6 w-6 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 17l6-6 4 4 8-8M14 7h7v7" />
                                </svg>
                            </div>
                            <h3 class="text-lg font-semibold text-white mb-2">Higher Google Maps Ranking</h3>
                            <p class="text-sm text-gray-400 mb-4 flex-1">
                                Review count, recency, and rating are top signals for the local 3-pack.
                                More reviews push you up the list.
                            </p>
                            <p class="text-sm font-semibold text-brand-accent">Climb the local 3-pack</p>
                        </div>
                        <div class="hidden md:flex absolute top-1/2 -right-3 lg:-right-2 -translate-y-1/2 z-10 h-8 w-8 items-center justify-center rounded-full bg-gray-900 ring-2 ring-brand-primary/40">
                            <svg class="h-4 w-4 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>

                    <!-- Step 3 -->
                    <div class="relative">
                        <div class="flex h-full flex-col rounded-2xl border border-gray-700 bg-gray-800/60 p-6 transition-all hover:border-brand-primary hover:-translate-y-1">
                            <div class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-primary/10 ring-1 ring-brand-primary/20">
                                <svg class="h-6 w-6 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <h3 class="text-lg font-semibold text-white mb-2">New Customers Find You</h3>
                            <p class="text-sm text-gray-400 mb-4 flex-1">
                                Customers searching "near me" call, click, and visit the businesses
                                Google shows them first — so being there matters.
                            </p>
                            <p class="text-sm font-semibold text-brand-accent">More calls, clicks &amp; visits</p>
                        </div>
                    </div>
                </div>

                <!-- Stat callout -->
                <div class="mt-12 mx-auto max-w-3xl">
                    <div class="rounded-2xl border border-brand-primary/20 bg-gradient-to-r from-brand-primary/10 via-gray-800/50 to-brand-secondary/10 p-6 sm:p-8 text-center">
                        <p class="text-base sm:text-lg text-gray-200 leading-relaxed mb-6">
                            <span class="font-bold text-white">88% of consumers</span>
                            use Google to discover local businesses — and the top 3 Maps results capture the vast majority of clicks.
                            Reviews are the fastest, cheapest way to earn that top spot.
                        </p>
                        <a
                            :href="bookDemoUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-block rounded-md bg-brand-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-secondary transition-colors"
                        >
                            See how it works for your business <span aria-hidden="true">→</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <!-- Features -->
        <section class="py-20 bg-gray-900">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-16">
                    <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">
                        Everything You Need to Get More Google Reviews
                    </h2>
                    <p class="text-xl text-gray-400 max-w-3xl mx-auto">
                        Powerful features that make review collection effortless
                    </p>
                </div>
                <FeatureGrid :features="keyFeatures" :columns="3" />
            </div>
        </section>

        <!-- Mid-page CTA banner -->
        <section class="py-12 bg-gray-900">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="rounded-2xl border border-brand-primary/30 bg-gradient-to-r from-brand-primary/15 via-gray-800/60 to-brand-secondary/15 px-6 py-8 sm:px-10">
                    <div class="flex flex-col lg:flex-row items-center justify-between gap-6 text-center lg:text-left">
                        <div>
                            <h3 class="text-2xl sm:text-3xl font-bold text-white mb-2">
                                Ready to grow your reviews?
                            </h3>
                            <p class="text-gray-300">
                                See a live demo and get a custom plan in 15 minutes.
                            </p>
                        </div>
                        <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0">
                            <a
                                :href="bookDemoUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="rounded-md bg-brand-primary px-6 py-3 text-center text-base font-semibold text-white shadow-sm hover:bg-brand-secondary transition-colors whitespace-nowrap"
                            >
                                Book a Free Demo <span aria-hidden="true">→</span>
                            </a>
                            <Link
                                v-if="canRegister"
                                :href="route('register')"
                                class="rounded-md border border-white/30 px-6 py-3 text-center text-base font-semibold text-white hover:bg-white/10 transition-colors whitespace-nowrap"
                            >
                                Get Started
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Before / After -->
        <section class="py-20 bg-gray-800 bg-opacity-30">
            <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-14">
                    <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">The Rep Jump Difference</h2>
                    <p class="text-xl text-gray-400 max-w-2xl mx-auto">
                        Same business. Same customers. Different system.
                    </p>
                </div>

                <div class="grid md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                    <!-- Without -->
                    <div class="relative flex flex-col rounded-2xl border border-gray-700 bg-gray-900/60 p-8">
                        <div class="mb-6 flex items-center gap-3">
                            <div class="flex h-11 w-11 items-center justify-center rounded-full bg-red-500/10 ring-1 ring-red-500/30">
                                <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <div>
                                <p class="text-xs font-semibold uppercase tracking-wider text-gray-500">The old way</p>
                                <h3 class="text-xl font-bold text-white">Without Rep Jump</h3>
                            </div>
                        </div>
                        <ul class="flex-1 space-y-4">
                            <li v-for="(item, i) in withoutItems" :key="i" class="flex items-start gap-3 text-gray-400">
                                <svg class="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{{ item }}</span>
                            </li>
                        </ul>
                        <div class="mt-8 border-t border-gray-700 pt-6">
                            <p class="text-xs uppercase tracking-wider text-gray-500">Average outcome</p>
                            <p class="mt-1 text-3xl font-bold text-red-400">2–3 reviews / month</p>
                        </div>
                    </div>

                    <!-- With -->
                    <div class="relative flex flex-col rounded-2xl border border-brand-primary/40 bg-gradient-to-br from-brand-primary/15 via-gray-800/60 to-gray-900/60 p-8 ring-1 ring-brand-primary/20 shadow-2xl shadow-brand-primary/10">
                        <div class="absolute -top-3 right-6 rounded-full bg-brand-primary px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-lg">
                            Recommended
                        </div>
                        <div class="mb-6 flex items-center gap-3">
                            <div class="flex h-11 w-11 items-center justify-center rounded-full bg-green-500/15 ring-1 ring-green-500/40">
                                <svg class="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <p class="text-xs font-semibold uppercase tracking-wider text-brand-accent">The Rep Jump way</p>
                                <h3 class="text-xl font-bold text-white">With Rep Jump</h3>
                            </div>
                        </div>
                        <ul class="flex-1 space-y-4">
                            <li v-for="(item, i) in withItems" :key="i" class="flex items-start gap-3 text-gray-200">
                                <svg class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{{ item }}</span>
                            </li>
                        </ul>
                        <div class="mt-8 border-t border-brand-primary/30 pt-6">
                            <p class="text-xs uppercase tracking-wider text-brand-accent">Average outcome</p>
                            <p class="mt-1 text-3xl font-bold text-white">10–20+ reviews / month</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Industries we serve -->
        <section class="py-20 bg-gray-900">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-12">
                    <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">Built for Local Businesses</h2>
                    <p class="text-xl text-gray-400 max-w-2xl mx-auto">
                        From the first appointment to the last service ticket, we work with the businesses that rely on word-of-mouth.
                    </p>
                </div>
                <IndustryGrid :industries="industries" />
            </div>
        </section>

        <!-- Testimonials -->
        <section class="py-20 bg-gray-800 bg-opacity-30">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-14">
                    <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">Real Results from Real Businesses</h2>
                    <p class="text-xl text-gray-400">See what happens when you automate your review collection</p>
                </div>
                <TestimonialGrid :items="testimonials" />
            </div>
        </section>

        <!-- Book a Demo -->
        <section id="book-demo" class="py-20 bg-gray-900 scroll-mt-24">
            <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="relative overflow-hidden rounded-3xl border border-brand-primary/30 bg-gradient-to-br from-brand-primary/20 via-gray-800/60 to-brand-secondary/20 p-10 sm:p-14 text-center shadow-2xl shadow-brand-primary/10">
                    <div class="pointer-events-none absolute -top-1/2 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-brand-primary/20 blur-3xl" aria-hidden="true"></div>

                    <div class="relative">
                        <div class="inline-flex items-center gap-2 rounded-full bg-brand-primary/10 px-3 py-1 text-sm font-medium text-brand-accent ring-1 ring-inset ring-brand-primary/20 mb-6">
                            📅 15 minutes · Zero pressure
                        </div>
                        <h2 class="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                            See Rep Jump in action
                        </h2>
                        <p class="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mb-8">
                            Book a free 15-minute call. We'll show you the product live, build a custom review growth plan for your business, and answer any questions.
                        </p>
                        <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                            <a
                                :href="bookDemoUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="rounded-md bg-brand-primary px-8 py-4 text-center text-base font-semibold text-white shadow-lg hover:bg-brand-secondary hover:shadow-xl transition-all transform hover:scale-105"
                            >
                                Book a Free 15-Min Demo <span aria-hidden="true">→</span>
                            </a>
                            <Link
                                v-if="canRegister"
                                :href="route('register')"
                                class="rounded-md border border-white/30 px-8 py-4 text-center text-base font-semibold text-white hover:bg-white/10 transition-colors"
                            >
                                Or get started now
                            </Link>
                        </div>

                        <p class="text-sm text-gray-400 mt-6">
                            Prefer email?
                            <a href="mailto:sales@repjump.com?subject=15-min%20Demo%20Request" class="text-brand-accent hover:underline">
                                sales@repjump.com
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section class="py-20 bg-gray-800 bg-opacity-30">
            <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-16">
                    <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
                    <p class="text-xl text-gray-400">Everything you need to know about getting more Google reviews</p>
                </div>
                <FAQSection :faqs="faqs" :default-open="0" />
            </div>
        </section>

        <!-- Final CTA -->
        <section class="py-20 bg-gradient-to-br from-brand-primary to-brand-secondary">
            <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 class="text-3xl sm:text-4xl font-bold text-white mb-6">
                    Ready to Get More Google Reviews?
                </h2>
                <p class="text-xl text-gray-100 mb-8">
                    Join 500+ businesses using Rep Jump to grow their online reputation
                </p>
                <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link
                        v-if="canRegister"
                        :href="route('register')"
                        class="inline-block py-4 px-8 bg-white text-brand-primary text-lg font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                        Get Started →
                    </Link>
                    <a
                        :href="bookDemoUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-block py-4 px-8 border-2 border-white text-white text-lg font-bold rounded-lg hover:bg-white/10 transition-colors"
                    >
                        Book a Demo
                    </a>
                </div>
                <p class="text-sm text-gray-100 mt-6">
                    ✓ Setup in 5 minutes • ✓ Cancel anytime
                </p>
            </div>
        </section>

        <!-- Trust badges -->
        <section class="py-12 bg-gray-900 border-t border-gray-800">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TrustBadges :badges="trustBadges" layout="horizontal" />
            </div>
        </section>

        <!-- Footer -->
        <footer class="py-16 text-center text-sm text-white/70 bg-gray-900">
            <div class="flex flex-col items-center space-y-4">
                <div class="flex space-x-6">
                    <a href="https://www.facebook.com/repjump" target="_blank" rel="noopener noreferrer" class="text-gray-400 hover:text-white transition" aria-label="Facebook">
                        <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                        </svg>
                    </a>
                    <a href="https://www.instagram.com/repjump" target="_blank" rel="noopener noreferrer" class="text-gray-400 hover:text-white transition" aria-label="Instagram">
                        <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
                        </svg>
                    </a>
                    <a href="https://x.com/repjumpagency" target="_blank" rel="noopener noreferrer" class="text-gray-400 hover:text-white transition" aria-label="X (Twitter)">
                        <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                    </a>
                </div>

                <div>Copyright &copy; {{ new Date().getFullYear() }} | Rep Jump</div>
                <div class="flex space-x-4">
                    <Link :href="route('terms.show')" class="hover:text-white transition">Terms of Service</Link>
                    <span>•</span>
                    <Link :href="route('policy.show')" class="hover:text-white transition">Privacy Policy</Link>
                    <span>•</span>
                    <button type="button" @click="openSupportModal" class="hover:text-white transition">Support</button>
                </div>
            </div>
        </footer>
    </div>

    <SupportFormModal :show="showSupportModal" @close="closeSupportModal" @submitted="handleSupportSubmitted" />

    <div v-if="showThankYouMessage" class="fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 transition-opacity duration-300">
        <div class="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <div>
                <p class="font-bold">Thank you for your interest!</p>
                <p class="text-sm">We'll be in touch with you shortly.</p>
            </div>
        </div>
    </div>
</template>
